'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { useCampaignRegisterMutation } from '@/hooks/react-query/community-v2/use-campaign-register.mutation'
import { useCreatorCampaignDetail } from '@/hooks/react-query/community-v2/use-creator-campaign-detail.query'
import { useRouter } from '@/i18n/navigation'
import {
  Campaign,
  CampaignProduct,
  CampaignRegistrationProduct,
  KolProfile,
} from '@/types/campaign'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { mapCreatorCampaignDetailToCampaign } from '../../_data/map-creator-campaign'
import { CampaignInfoSummary } from './campaign-info-summary'
import {
  CampaignProductPicker,
  SelectedProductsMap,
} from './campaign-product-picker'
import { CampaignProductSummaryList } from './campaign-product-summary-list'
import {
  CampaignRegisterConfirm,
  ConfirmProductItem,
} from './campaign-register-confirm'
import { CampaignSelectedProductsStrip } from './campaign-selected-products-strip'
import { CampaignShippingEditModal } from './campaign-shipping-edit-modal'

interface CampaignRegisterModalProps {
  campaign: Campaign
  kolProfile: KolProfile
  open: boolean
  onOpenChange: (open: boolean) => void
}

type ModalStep = 'products' | 'confirm' | 'success'

export const CampaignRegisterModal = ({
  campaign,
  kolProfile,
  open,
  onOpenChange,
}: CampaignRegisterModalProps) => {
  const t = useTranslations('community_threads.campaign.register_modal')
  const router = useRouter()
  const queryClient = useQueryClient()
  const [selectedProducts, setSelectedProducts] = useState<SelectedProductsMap>(
    {}
  )
  const [showProductsError, setShowProductsError] = useState(false)
  // Đếm tăng dần để sinh rowId duy nhất cho mỗi tổ hợp màu/size được thêm -
  // đủ dùng làm React key và để xoá đúng dòng, không cần random UUID.
  const rowIdCounterRef = useRef(0)
  const [step, setStep] = useState<ModalStep>('products')
  const [pendingProducts, setPendingProducts] = useState<
    CampaignRegistrationProduct[]
  >([])
  const [confirmItems, setConfirmItems] = useState<ConfirmProductItem[]>([])
  const [isEditShippingOpen, setIsEditShippingOpen] = useState(false)
  const productsSectionRef = useRef<HTMLDivElement>(null)
  // Sửa thông tin giao hàng ở màn xác nhận (qua popup "Cập nhật thông tin"
  // hoặc "Chọn từ sổ địa chỉ") chỉ áp dụng cho lượt đăng ký này - không ghi
  // đè lên hồ sơ KOL dùng chung cho các lần đăng ký khác.
  const [confirmProfileOverride, setConfirmProfileOverride] =
    useState<KolProfile | null>(null)
  const confirmKolProfile = confirmProfileOverride ?? kolProfile

  const { mutateAsync, isPending } = useCampaignRegisterMutation()

  const { data: campaignDetail, isLoading: isDetailLoading } =
    useCreatorCampaignDetail(campaign.slug, open)

  const displayCampaign = useMemo(
    () =>
      campaignDetail
        ? mapCreatorCampaignDetailToCampaign(campaignDetail)
        : campaign,
    [campaignDetail, campaign]
  )

  // Danh sách phẳng tất cả tổ hợp màu/size đã chọn (của mọi sản phẩm), render
  // dạng bảng tóm tắt giống hệt màn xác nhận - hiển thị gộp 1 chỗ bên dưới
  // carousel thay vì bên trong từng item, để không đẩy giãn chiều cao ảnh
  // khi chọn nhiều tổ hợp.
  const selectedRowsFlat: ConfirmProductItem[] = useMemo(
    () =>
      Object.entries(selectedProducts).flatMap(([productId, rows]) => {
        const product = displayCampaign.products.find((p) => p.id === productId)
        return rows.map((row) => ({
          productId,
          rowId: row.rowId,
          name: product?.name || '',
          image: product?.image || '',
          color: row.color,
          size: row.size,
          price: product?.price || 0,
        }))
      }),
    [selectedProducts, displayCampaign.products]
  )

  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset transient state when reopening modal for a (possibly different) campaign
    setSelectedProducts({})
    setShowProductsError(false)
    setStep('products')
    setPendingProducts([])
    setConfirmItems([])
    setConfirmProfileOverride(null)
  }, [open, campaign.id])

  // Thêm 1 tổ hợp màu/size mới cho sản phẩm - mỗi tổ hợp là 1 dòng riêng,
  // số lượng cố định 1. CampaignProductItem đã tự validate variant tồn tại
  // và không trùng tổ hợp đã chọn trước khi gọi handler này.
  const handleAddVariant = (
    product: CampaignProduct,
    color: string,
    size: string
  ) => {
    const variant = displayCampaign.products
      .find((p) => p.id === product.id)
      ?.variants?.find((v) => v.color === color && v.size === size)
    if (!variant) return

    rowIdCounterRef.current += 1
    const rowId = `${product.id}-${rowIdCounterRef.current}`

    setSelectedProducts((prev) => ({
      ...prev,
      [product.id]: [
        ...(prev[product.id] ?? []),
        { rowId, color, size, variantId: variant.id },
      ],
    }))
    setShowProductsError(false)
  }

  const handleRemoveVariant = (productId: string, rowId: string) => {
    setSelectedProducts((prev) => {
      const rows = (prev[productId] ?? []).filter((row) => row.rowId !== rowId)
      const next = { ...prev }
      if (rows.length > 0) {
        next[productId] = rows
      } else {
        delete next[productId]
      }
      return next
    })
  }

  // Validate lựa chọn sản phẩm rồi chuyển sang màn xác nhận thông tin giao
  // hàng + sản phẩm trước khi thực sự gọi API. Mỗi tổ hợp màu/size đã được
  // resolve variant_id thật ngay lúc thêm (handleAddVariant), nên ở đây chỉ
  // cần gộp lại và kiểm tra có ít nhất 1 tổ hợp được chọn.
  const handleReview = () => {
    const rows = Object.entries(selectedProducts).flatMap(([productId, list]) =>
      list.map((row) => ({ productId, ...row }))
    )

    if (rows.length === 0) {
      setShowProductsError(true)
      productsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      return
    }

    const products: CampaignRegistrationProduct[] = rows.map((row) => {
      const product = displayCampaign.products.find(
        (p) => p.id === row.productId
      )
      return {
        productId: row.productId,
        productName: product?.name || '',
        slug: product?.slug || '',
        variantId: row.variantId,
        color: row.color,
        size: row.size,
      }
    })

    const items: ConfirmProductItem[] = rows.map((row) => {
      const product = displayCampaign.products.find(
        (p) => p.id === row.productId
      )
      return {
        productId: row.productId,
        name: product?.name || '',
        image: product?.image || '',
        color: row.color,
        size: row.size,
        price: product?.price || 0,
      }
    })

    setPendingProducts(products)
    setConfirmItems(items)
    setStep('confirm')
  }

  const handleConfirmSubmit = async () => {
    try {
      await mutateAsync({
        campaignId: displayCampaign.id,
        campaignTitle: displayCampaign.title,
        ...confirmKolProfile,
        products: pendingProducts,
      })
      // Cập nhật lại trạng thái "đã đăng ký" cho listing (community-threads
      // lẫn trang FOC account) ngay sau khi đăng ký thành công.
      queryClient.invalidateQueries({ queryKey: ['my-campaign-registrations'] })
      setStep('success')
    } catch {
      toast.error(t('submit_error'))
    }
  }

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent
        className='gap-0 border-none p-0 lg:max-h-[85dvh] lg:max-w-5xl'
        aria-describedby='modal-campaign-register'
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />

        {step === 'success' ? (
          <div className='flex max-h-[90dvh] flex-col gap-4 overflow-hidden pt-4 lg:max-h-[85dvh]'>
            <h3 className='shrink-0 px-5 font-criteria text-xl font-bold text-neutral-900 md:text-2xl lg:px-8'>
              {t('success_title')}
            </h3>

            <div className='no-scrollbar min-h-0 flex-1 space-y-6 overflow-y-auto px-5 lg:px-8'>
              <div className='flex items-center gap-2 rounded-lg bg-primary-500/[0.07] p-3'>
                <AlertCircle size={20} className='shrink-0 text-primary-500' />
                <p className='text-sm text-neutral-900 md:text-base'>
                  {t('success_pending_note')}
                </p>
              </div>

              <CampaignInfoSummary
                campaign={displayCampaign}
                badge={
                  <span className='inline-block rounded-full bg-success-50 px-3 py-1 text-[10px] font-bold uppercase text-success-400 md:text-xs'>
                    {t('success_badge')}
                  </span>
                }
              />

              <div>
                <div className='mb-3 flex items-center justify-between gap-2'>
                  <h5 className='font-criteria text-base font-bold text-neutral-900 md:text-xl'>
                    {t('success_profile_title')}
                  </h5>
                  <button
                    type='button'
                    onClick={() => {
                      onOpenChange(false)
                      router.push('/account/friends-of-coolmate')
                    }}
                    className='shrink-0 text-sm font-medium text-primary hover:opacity-80'
                  >
                    {t('success_change_info')}
                  </button>
                </div>
                <div className='space-y-3 text-sm text-neutral-700 md:text-base'>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_fullname')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.fullName}
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_phone')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.phone}
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_email')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.email}
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_address')}
                    </span>
                    <span className='text-neutral-900'>
                      {[
                        confirmKolProfile.address,
                        confirmKolProfile.ward,
                        confirmKolProfile.district,
                        confirmKolProfile.city,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_social')}
                    </span>
                    <div className='flex flex-wrap gap-2'>
                      {confirmKolProfile.socialLinks.map((link) => (
                        <span
                          key={link}
                          className='rounded bg-neutral-200 px-2 py-0.5 text-neutral-800'
                        >
                          {link}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_sports')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.sports}
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_height')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.height}cm
                    </span>
                  </div>
                  <div className='flex gap-6'>
                    <span className='w-[140px] shrink-0 text-neutral-500 md:w-[200px]'>
                      {t('success_field_weight')}
                    </span>
                    <span className='text-neutral-900'>
                      {confirmKolProfile.weight}kg
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className='mb-3 font-criteria text-base font-bold text-neutral-900 md:text-xl'>
                  {t('success_products_title')}
                </h5>
                <CampaignProductSummaryList
                  title={t('success_products_column_label')}
                  items={confirmItems}
                />
              </div>
            </div>

            <div className='h-px w-full shrink-0 bg-neutral-200' />

            {/* Footer luôn cố định trong khung nhìn thực (flex-col + max-h-[90dvh]
            trên div cha, shrink-0 ở đây) - vùng nội dung phía trên tự co lại và
            tự cuộn để nhường chỗ, thay vì phụ thuộc vào 1 con số vh cố định. */}
            <div className='flex shrink-0 gap-3 px-5 pb-2 md:pb-6 lg:px-8'>
              <Button
                variant='outline-neutral'
                className='flex-1'
                onClick={() => onOpenChange(false)}
              >
                {t('close')}
              </Button>
              <Link href='/account/friends-of-coolmate' className='flex-1'>
                <Button variant='black' className='w-full'>
                  {t('success_view_detail')}
                </Button>
              </Link>
            </div>
          </div>
        ) : step === 'confirm' ? (
          <>
            <CampaignRegisterConfirm
              kolProfile={confirmKolProfile}
              items={confirmItems}
              isSubmitting={isPending}
              onBack={() => setStep('products')}
              onConfirm={handleConfirmSubmit}
              onEditAddress={() => setIsEditShippingOpen(true)}
              onProfileUpdate={setConfirmProfileOverride}
            />
            <CampaignShippingEditModal
              open={isEditShippingOpen}
              onOpenChange={setIsEditShippingOpen}
              kolProfile={confirmKolProfile}
              onSave={setConfirmProfileOverride}
            />
          </>
        ) : (
          <div className='flex max-h-[90dvh] flex-col gap-4 overflow-hidden pt-4 lg:max-h-[85dvh]'>
            <h3 className='shrink-0 px-5 font-criteria text-xl font-bold text-neutral-900 md:text-2xl lg:px-8'>
              {t('title')}
            </h3>

            <div className='no-scrollbar min-h-0 flex-1 space-y-6 overflow-y-auto px-5 lg:px-8'>
              <CampaignInfoSummary
                campaign={displayCampaign}
                badge={
                  <span className='inline-block rounded-full bg-primary-500 px-3 py-1 text-[10px] font-bold uppercase text-white md:text-xs'>
                    {displayCampaign.statusLabel}
                  </span>
                }
              />

              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div className='rounded-lg bg-neutral-50 p-3'>
                  <h5 className='mb-1 font-criteria text-sm font-bold text-neutral-900 md:text-base'>
                    {t('tasks_title')}
                  </h5>
                  <ul className='list-disc space-y-1 pl-5 text-xs text-neutral-700 md:text-sm'>
                    {displayCampaign.tasks.map((task, idx) => (
                      <li key={idx}>{task}</li>
                    ))}
                  </ul>
                </div>
                <div className='rounded-lg bg-primary-500/[0.07] p-3'>
                  <h5 className='mb-1 font-criteria text-sm font-bold text-neutral-900 md:text-base'>
                    {t('benefits_title')}
                  </h5>
                  <ul className='list-disc space-y-1 pl-5 text-xs text-neutral-700 md:text-sm'>
                    {displayCampaign.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className='h-px w-full bg-neutral-200' />

              <div ref={productsSectionRef}>
                {isDetailLoading ? (
                  <>
                    <h5 className='mb-3 font-criteria text-base font-bold text-neutral-900 md:text-xl'>
                      {t('products_title')}
                    </h5>
                    <div className='flex items-center justify-center gap-2 rounded-2xl bg-neutral-50 py-10 text-sm text-neutral-500'>
                      <Loader2 className='h-4 w-4 animate-spin' />
                      {t('loading_products')}
                    </div>
                  </>
                ) : (
                  <CampaignProductPicker
                    title={
                      <h5 className='font-criteria text-base font-bold text-neutral-900 md:text-xl'>
                        {t('products_title')}
                      </h5>
                    }
                    products={displayCampaign.products}
                    selectedProducts={selectedProducts}
                    onAddVariant={handleAddVariant}
                  />
                )}
                {showProductsError && (
                  <p className='mt-2 text-sm text-error-500'>
                    {t('products_error')}
                  </p>
                )}

                {selectedRowsFlat.length > 0 && (
                  <div className='mt-4'>
                    <CampaignSelectedProductsStrip
                      title={t('selected_variants_title')}
                      countLabel={t('selected_variants_count', {
                        count: selectedRowsFlat.length,
                      })}
                      items={selectedRowsFlat}
                      removeAriaLabel={t('remove_variant_aria')}
                      onRemove={(item) =>
                        item.rowId &&
                        handleRemoveVariant(item.productId, item.rowId)
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            <div className='h-px w-full shrink-0 bg-neutral-200' />

            {/* Footer luôn cố định trong khung nhìn thực (flex-col + max-h-[90dvh]
            trên div cha, shrink-0 ở đây) - vùng nội dung phía trên tự co lại và
            tự cuộn để nhường chỗ, thay vì phụ thuộc vào 1 con số vh cố định. */}
            <div className='flex shrink-0 flex-col gap-3 px-5 pb-2 sm:flex-row sm:items-center sm:justify-between md:pb-6 lg:px-8'>
              <div className='flex items-center gap-2 text-sm text-neutral-800 md:text-base'>
                <AlertCircle size={20} className='shrink-0' />
                {t('approval_note')}
              </div>
              <Button
                variant='black'
                size='large'
                className='sm:w-[280px]'
                onClick={handleReview}
              >
                {t('submit')}
              </Button>
            </div>
          </div>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
